import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HeartPulse, Inbox, MessagesSquare, TriangleAlert, Users } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { normalizeUrgency, isAtLeast } from "@/lib/urgency";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { LinkButton } from "@/components/ui/Button";
import { UrgencyBadge } from "@/components/ui/Badge";
import { StatTile } from "@/components/ui/StatTile";
import { Alert, EmptyState, LoadingPanel } from "@/components/ui/Feedback";

const overviewSchema = z.object({
  totals: z.object({
    conversations: z.number(),
    patients: z.number(),
    open_alerts: z.number(),
    appointments_today: z.number(),
  }),
  monthly: z
    .array(z.object({ label: z.string(), value: z.number() }))
    .default([]),
  alerts: z
    .array(
      z.object({
        id: z.number(),
        patient_rm: z.string(),
        patient_name: z.string().nullable().default(null),
        severity: z.string(),
        message: z.string(),
        created_at: z.string(),
      }),
    )
    .default([]),
});

export function AdminOverview() {
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: ({ signal }) => api.get("/api/admin/overview", overviewSchema, signal),
    // Alert counts drive triage decisions, so this view refreshes on its own.
    refetchInterval: 60_000,
  });

  if (isPending) {
    return (
      <>
        <PageHeader title="Overview" />
        <Card>
          <LoadingPanel label="Memuat ringkasan operasional" />
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Overview" />
        <Alert tone="danger" title="Gagal memuat data">
          {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
        </Alert>
      </>
    );
  }

  const criticalAlerts = data.alerts.filter((alert) =>
    isAtLeast(normalizeUrgency(alert.severity), "urgent"),
  );

  return (
    <>
      <PageHeader
        eyebrow="Ringkasan operasional"
        title="Overview"
        description="Kondisi layanan hari ini dan antrean yang menunggu tindakan."
      />

      {/* An unhandled emergency alert outranks every metric on this page. */}
      {criticalAlerts.length > 0 && (
        <Alert tone="danger" title={`${criticalAlerts.length} alert perlu tindakan segera`} className="mb-5">
          Pasien dengan tanda bahaya menunggu konfirmasi di Monitoring Kesehatan.
        </Alert>
      )}

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Alert terbuka"
          value={data.totals.open_alerts}
          caption="belum ditangani"
          icon={<TriangleAlert className="size-4" />}
        />
        <StatTile
          label="Percakapan"
          value={data.totals.conversations}
          caption="total tersimpan"
          icon={<MessagesSquare className="size-4" />}
        />
        <StatTile
          label="Pasien terdaftar"
          value={data.totals.patients}
          icon={<Users className="size-4" />}
        />
        <StatTile
          label="Janji temu hari ini"
          value={data.totals.appointments_today}
          icon={<HeartPulse className="size-4" />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader eyebrow="12 bulan terakhir" title="Volume percakapan" />
          <CardBody>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthly} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="adminTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-flux-400)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--color-flux-400)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 5" vertical={false} stroke="var(--hairline)" />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "var(--content-subtle)", fontSize: 11 }}
                    dy={6}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "var(--content-subtle)", fontSize: 11 }}
                  />
                  <Tooltip
                    cursor={{ stroke: "var(--hairline-strong)", strokeWidth: 1 }}
                    contentStyle={{
                      background: "var(--surface)",
                      border: "1px solid var(--hairline-strong)",
                      borderRadius: "0.75rem",
                      fontSize: "13px",
                      boxShadow: "var(--shadow-float)",
                      color: "var(--content)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-flux-600)"
                    strokeWidth={2}
                    fill="url(#adminTrend)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            eyebrow="Perlu tindakan"
            title="Alert terbaru"
            action={
              <LinkButton to="/admin/health-monitoring" size="sm" variant="ghost">
                Semua
              </LinkButton>
            }
          />
          {data.alerts.length === 0 ? (
            <EmptyState
              icon={<Inbox className="size-5" />}
              title="Tidak ada alert terbuka"
              description="Semua pemeriksaan pasien dalam batas aman."
            />
          ) : (
            <ul className="divide-hairline divide-y">
              {data.alerts.slice(0, 6).map((alert) => (
                <li key={alert.id} className="px-5 py-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {alert.patient_name ?? alert.patient_rm}
                      </p>
                      <p className="text-content-muted mt-0.5 text-xs">{alert.message}</p>
                    </div>
                    <UrgencyBadge level={normalizeUrgency(alert.severity)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
